import { getS3File, getS3Object } from '@meteor/frontend-core';
import { readBuffer, readBufferWithStyles } from '../../utils/xlsxReader';
import IndexedDBLocalStorage from '../../utils/indexDbStorage';
import { cloneDeep, isNumber, uniq } from 'lodash';
import { Space, message, Input } from 'antd';
import dayjs from 'dayjs';

const defaultPath = 'default';
const defaultFile = 'mock-data-template.xlsx';
const bucketName = process.env.REACT_APP_BUCKET_NAME;

const indexDbStorage = new IndexedDBLocalStorage();

export const readExcelFileAndRecord = async (subpath: string, fileName: string) => {
  let buffer = '';
  try {
    buffer = await getS3File(`${bucketName}/${subpath.trim()}`, fileName.trim());
  } catch (error) {
    console.error(error);
    message.error(`file ${bucketName}/${subpath} download failed. please check the file path.`);
    buffer = await getS3File(`${bucketName}/${defaultPath}`, defaultFile);
  }
  const data = await readBuffer(buffer);
  if (data) {
    await indexDbStorage.setItem('excelData', JSON.stringify(data));
  }
  return data;
};

export const readExcelFileWithStylesAndRecord = async (subpath: string, fileName: string) => {
  let buffer = '';
  try {
    buffer = await getS3File(`${bucketName}/${subpath.trim()}`, fileName.trim());
  } catch (error) {
    buffer = await getS3File(`${bucketName}/${defaultPath}`, defaultFile);
  }
  const data = await readBufferWithStyles(buffer);
  if (data) {
    await indexDbStorage.setItem('excelDataWithStyles', JSON.stringify(data));
  }
  return data;
};

export const getUserDefaultData = async (email: string) => {
  const resp = await getS3Object(bucketName, 'admin-config.json');
  const demoProjectIndex = await indexDbStorage.getItem('demoProjectIndex');
  let index = 0;
  if (demoProjectIndex) {
    index = Number(demoProjectIndex) || 0;
  }

  const dataList = resp.userDefaultData.filter((item) => {
    return item.Email === email;
  });

  if (dataList && dataList.length > 0) {
    const result = [];
    dataList.forEach((dataInfo) => {
      const project = resp.projects
        ? resp.projects.find((p) => {
            return p.projectName.trim() === dataInfo.defaultPage.trim() || p.defaultRoute.trim() === dataInfo.defaultPage.trim();
          })
        : {
            projectTitle:
              dataInfo.defaultPage !== '/bottleneck-monitoring' ? 'AI Powered Management Cockpit' : 'Dynamic Bottleneck Monitoring',
            logoUrl: '/assets/accenture_icon.png',
          };
      result.push({ ...dataInfo, project: project });
    });
    return result[index] ? result[index] : result[0];
  } else {
    return {
      subpath: defaultPath,
      fileName: defaultFile,
      defaultPage: '/',
      project: {
        projectTitle: 'AI Powered Management Cockpit',
        logoUrl: '/assets/accenture_icon.png',
      },
    };
  }
};

export const getUserDataList = async (email: string) => {
  const resp = await getS3Object(bucketName, 'admin-config.json');
  const dataList = resp.userDefaultData.filter((item) => {
    return item.Email === email;
  });

  if (dataList && dataList.length > 0) {
    const result = [];
    dataList.forEach((dataInfo) => {
      const project = resp.projects
        ? resp.projects.find((p) => {
            return p.projectName.trim() === dataInfo.defaultPage.trim() || p.defaultRoute.trim() === dataInfo.defaultPage.trim();
          })
        : {
            projectTitle:
              dataInfo.defaultPage !== '/bottleneck-monitoring' ? 'AI Powered Management Cockpit' : 'Dynamic Bottleneck Monitoring',
            logoUrl: '/assets/accenture_icon.png',
          };
      result.push({ ...dataInfo, project: project });
    });
    return result;
  } else {
    return [
      {
        subpath: defaultPath,
        fileName: defaultFile,
        defaultPage: '/',
        project: {
          projectTitle: 'AI Powered Management Cockpit',
          logoUrl: '/assets/accenture_icon.png',
        },
      },
    ];
  }
};

/***
 * 设定table layout
 *
 */
export const formatTableFun = (tableData, tableOptions?) => {
  /***
   * 获取对象key，并排序
   * A,B,C...AA,AB...
   */
  const getKeys = (rowObj) => {
    let keys = Object.keys(rowObj);
    keys.sort((a: any, b: any) => {
      if (a.length === b.length && a > b) {
        return 1;
      } else if (a.length !== b.length && a.length < b.length) {
        return -1;
      } else if (a.length !== b.length && a.length > b.length) {
        return 1;
      } else {
        return -1;
      }
    });
    let i = keys.length - 1;
    while (i > 0) {
      if (!rowObj[keys[i]]['bgColor'] && !rowObj[keys[i]]['value']) {
        keys.pop();
      } else {
        return keys;
      }
      i--;
    }
  };

  /***
   * 获取excel 表头区域
   * 暂时只看A列。有需要可调整全范围
   *
   */
  const getTitleRowCount = () => {
    let i = 0;
    while (i < tableData.length - 1) {
      if (tableData[i]['A'].bgColor === TITLE_AREA_COLOR) {
        i++;
        continue;
      }
      break;
    }
    return i - 1;
  };

  /***
   * 获取excel 表头区域
   * 暂时只看A列。有需要可调整全范围
   *
   */
  const getFixCount = () => {
    let i = 1;
    while (i < keys.length) {
      if (!tableData[0][keys[i]].value) {
        i++;
      } else {
        break;
      }
    }
    if (i === keys.length) {
      return 0;
    }
    return i;
  };

  const getTableColumns = () => {
    // 日文汉字
    var kannjiReg = /[\u3040-\u30FF\u31F0-\u31FF\uFF00-\uFFEF\u4E00-\u9FAF]/g;
    // A-Z
    var wAZReg = /[A-Z]/g;
    // a-z
    var wazReg = /[a-z]/g;
    // 空格及换行
    var blankReg = /[\f\n\r\t\v\u0020\u00a0\u1680\u180e\u2000-\u200a\u2028\u2029\u202f\u205f\u3000\ufeff]/g;
    // % \ / 可追加 追加规则（ \追加字符 )
    var characterReg = /[\%\\\/]/g;
    // . -
    var punctuationReg = /[\.\-]/g;
    // 数字
    var numberReg = /[0-9]/g;
    var dotReg = /[,，、]/g;

    // 以上未包含字符默认宽度
    const OTHER_CHARACTER_WIDTH = 120;

    // table 单元格左右padding宽度
    const TABLE_CELL_PADDING = 160;
    // 折叠型table,第一列，子元素缩进宽度
    const FIRST_COLUMN_LEVEL_BLANK = 320;

    // 字符等比缩放后，base宽度（10倍放大，调整精细度可100放大，基础宽度设为0.01）
    const BASE_CHARTER_WIDTH = 0.1;

    const SORT_WIDTH = 10;

    const columnWidthList = new Set();
    let tableWidth = 0;
    // 记录已处理列下标
    let finishKeyIndex;

    /***
     * 默认单元格渲染方式
     * 包含单元格背景颜色
     *
     */
    const itemRenderFunction = () => {
      return (val, record, index) => {
        if (!val) {
          return '';
        } else if (val.value.startsWith('Input-')) {
          if (val.value.endsWith('%')) {
            return (
              <div>
                <Input
                  className="text-right"
                  size="small"
                  addonAfter={'%'}
                  style={{ lineHeight: 1 }}
                  defaultValue={val.value.substring(6).replace('%', '')}
                />
              </div>
            );
          } else {
            return (
              <div>
                <Input className="text-right" size="small" style={{ lineHeight: 1 }} defaultValue={val.value.substring(6)} />
              </div>
            );
          }
        }

        return val.bgColor ? (
          <div
            style={{
              background: '#' + val.bgColor,
            }}
          >
            {val.value || '-'}
          </div>
        ) : (
          val.value || '-'
        );
      };
    };

    /***
     * 循环重新设定宽度属性
     *
     */
    const loopColumnChildrenAndResetWidth = (parentItem) => {
      let item = parentItem;
      if (item.children && parentItem.children.length > 0) {
        for (let i = 0; i < item.children.length; i++) {
          loopColumnChildrenAndResetWidth(item.children[i]);
        }
      } else {
        let newWidthList = setWidthToSame();
        let newIndex = newWidthList.findIndex((nWidth) => nWidth.oldWidth === item.width);
        if (newIndex !== -1) {
          item.width = newWidthList[newIndex].newWidth;
        }
      }
    };

    /***
     * 调整宽度
     *
     */
    const resetColumnWidth = (tableColumnsO) => {
      const tableColumnsN = cloneDeep(tableColumnsO);
      for (let i = 0; i < tableColumnsN.length; i++) {
        if (tableColumnsN[i].fixed) {
          continue;
        } else {
          loopColumnChildrenAndResetWidth(tableColumnsN[i]);
        }
      }
      return tableColumnsN;
    };

    /***
     * 将宽度设定至相同
     * 根据近似宽度将宽度归类
     * （暂定原则为宽度不超过30）
     *
     */
    const setWidthToSame = () => {
      const widthArray = Array.from(columnWidthList) as number[];
      const arrayLastIndex = widthArray.length - 1;
      const curseIndexList = [];
      widthArray.sort((a, b) => (a > b ? 1 : -1));
      for (let i = 0; i < widthArray.length - 1; i++) {
        for (let j = arrayLastIndex; j > 0; j--) {
          if (widthArray[i] > widthArray[j] - 30) {
            curseIndexList.push(j);
            i = j;
            break;
          }
        }
      }
      const newWidthObjList = widthArray.reduce((a, b, index) => {
        for (let i = 0; i < curseIndexList.length; i++) {
          if (index <= curseIndexList[i]) {
            a.push({
              oldWidth: b,
              newWidth: widthArray[curseIndexList[i]],
            });
          }
        }
        return a;
      }, []);
      return newWidthObjList;
    };

    // 列宽计算

    /***
     * 根据单元格内容设定宽度
     *
     * 同比扩大字符宽度10倍，可按需扩张至100，倍数提高能够增加精细度的提高
     */
    const formatValueToLength = (valueStr): number => {
      let text = valueStr;
      if (valueStr.includes('-skipSort')) {
        console.log(valueStr);
        text = text.replace('-skipSort', '');
      } else if (valueStr.includes('-sort')) {
        text = text.replace('-sort', '');
      }
      text = text.replaceAll(',', '.'); // 替换所有,->, （为后续截断做准备）
      text = text.replaceAll(numberReg, '100,'); // 替换所有数字为宽度10
      text = text.replaceAll(punctuationReg, '37,'); // 替换所有标点为宽度3.7
      text = text.replaceAll(characterReg, '120,'); // 替换所有特殊字符为宽度12
      text = text.replaceAll(blankReg, '37,'); // 替换所有空格换行字符为宽度3.7
      text = text.replaceAll(wazReg, '100,'); // 替换所有小写字母为宽度10
      text = text.replaceAll(wAZReg, '120,'); // 替换所有大写字母为宽度12
      text = text.replaceAll(kannjiReg, '180,'); // 替换所有日语汉字为宽度20
      const widthList = text.split(','); // 按照，分割宽度
      // 累加宽度和并返回
      return widthList.reduce((a, b) => {
        if (isNaN(Number(b))) {
          // 含有以上未包含字符设定默认宽度
          return Number(a) + OTHER_CHARACTER_WIDTH;
        } else {
          return Number(a) + Number(b);
        }
      }, 0) as number;
    };

    /***
     * 设定跨行
     *
     */
    const checkRowSpan = (tmpData, rowIndex, key, keyIndex) => {
      let rSpan = -1;
      if (rowIndex + 1 !== tmpData.length && !tmpData[rowIndex + 1][key]?.value) {
        rSpan = 1;
        for (let i = rowIndex; i < tmpData.length; i++) {
          if (i + 1 !== tmpData.length && !tmpData[i + 1][key]?.value) {
            for (let j = 0; j < keyIndex; j++) {
              if (tmpData[i + 1][keys[j]]?.value) {
                return rSpan;
              }
            }
            rSpan++;
          } else {
            break;
          }
        }
      }
      return rSpan;
    };

    /***
     * 设定跨列
     *
     */
    const checkCellSpan = (tmpData, record, rowIndex, key, keyIndex) => {
      let cSpan = 1;
      let rSpan = -1;
      for (let i = keyIndex; i < fixCount; i++) {
        if (i + 1 !== fixCount && record[keys[i + 1]].value === '') {
          cSpan++;
        } else {
          break;
        }
      }
      rSpan = checkRowSpan(tmpData, rowIndex, key, keyIndex);
      if (rSpan === -1) {
        return { colSpan: cSpan };
      } else {
        return { colSpan: cSpan, rowSpan: rSpan };
      }
    };

    /***
     * 单元格跨行跨列处理
     * 优先跨行
     *
     */
    const setColumnCell = (key, keyIndex) => {
      let tmpData = cloneDeep(tableData);
      tmpData.splice(0, titleRowCount + 1);
      return (record, rowIndex) => {
        if (record[key].value !== '') {
          if (keyIndex + 1 !== fixCount && record[keys[keyIndex + 1]].value === '') {
            return checkCellSpan(tmpData, record, rowIndex, key, keyIndex);
          }
          if (rowIndex + 1 !== tmpData.length && tmpData[rowIndex + 1] && !tmpData[rowIndex + 1][key]?.value) {
            let rSpan = 1;
            rSpan = checkRowSpan(tmpData, rowIndex, key, keyIndex);
            return { rowSpan: rSpan };
          }
          return { colSpan: 1 };
        } else {
          for (let i = 0; i < keyIndex; i++) {
            if (record[keys[i]].value !== '') {
              return { colSpan: 0 };
            } else {
              return { rowSpan: 0 };
            }
          }
          return { rowSpan: 0 };
        }
      };
    };

    /***
     * 默认列宽度设定函数
     *
     */
    const setColumnWidth = (key, keyIndex) => {
      let tmpData = cloneDeep(tableData);
      let maxLength = 0;
      let tmpLength = 0;
      // 查找最宽单元格
      for (let i = 0; i < tmpData.length; i++) {
        if (!tmpData[i][key]?.value || (keyIndex + 1 < keys.length && !tmpData[i][keys[keyIndex + 1]]?.value)) {
          continue;
        } else {
          tmpLength = formatValueToLength(tmpData[i][key].value);
          if (tmpLength > maxLength) {
            maxLength = tmpLength;
          }
        }
      }
      maxLength = maxLength + TABLE_CELL_PADDING;
      let columnWidth = BASE_CHARTER_WIDTH * maxLength;
      if (keyIndex >= fixCount) {
        columnWidthList.add(columnWidth);
      }
      tableWidth += columnWidth;
      return columnWidth;
    };

    /***
     * 设定列属性
     *
     */
    const setColumnProperties = (item, key, keyIndex) => {
      item.key = key;
      item.width = setColumnWidth(key, keyIndex);
      item.dataIndex = key;
      item.render = itemRenderFunction();
      if (item.title.includes('-filter')) {
        const tmpData = cloneDeep(tableData);
        const header = tmpData.shift();
        let filterKey = '';
        for (const key in header) {
          if (Object.prototype.hasOwnProperty.call(header, key)) {
            const element = header[key];
            if (element.value === item.title) {
              filterKey = key;
            }
          }
        }
        item.filters = uniq(
          tmpData.map((el) => {
            return el[filterKey].value;
          })
        ).map((value) => {
          return { text: value, value: value };
        });
        item.title = item.title.replace('-filter', '');
        item.filterSearch = true;
        item.onFilter = (value, record) => {
          return record[filterKey].value.startsWith(value as string);
        };
      }
      if (item.title.includes('-skipSort')) {
        item.title = item.title.replace('-skipSort', '');
      } else if (!disableSort || item.title.includes('-sort')) {
        // todo
        item.title = item.title.replace('-sort', '');
        item.width = item.width + SORT_WIDTH;
        item.sorter = (a, b) => {
          if (dayjs(a[key].value, 'YYYY/MM/DD', true).isValid()) {
            return new Date(a[key].value).getTime() - new Date(b[key].value).getTime();
          } else {
            const symbolList = [',', '%', '¥'];
            const removeSymbol = (str) => {
              let result = str;
              for (let index = 0; index < symbolList.length; index++) {
                const symbol = symbolList[index];
                result = result.replaceAll(symbol, '');
              }
              const num = parseFloat(result);
              return isNaN(num) ? result : num;
            };
            const aF = removeSymbol(a[key].value);
            const bF = removeSymbol(b[key].value);
            if (isNumber(aF) && isNumber(bF)) {
              return aF - bF;
            }
            return aF.localeCompare(bF);
          }
        };
      }
      finishKeyIndex = keyIndex;
      return item;
    };

    /***
     * 设定column阶层（table header多层的情况）
     *
     */
    const setItemChildren = (nextItem, key, keyIndex, currentRow) => {
      if (nextItem.value) {
        let childrenCount = 0;
        for (let i = keyIndex + 1; i < keys.length; i++) {
          if (!tableData[currentRow][keys[i]]?.value) {
            childrenCount++;
          } else {
            break;
          }
        }
        let item = {} as any;
        let childrenList = [];
        const titles = nextItem.value.split('(left)');
        item.title = titles[0];
        item.className = titles.length > 1 ? '' : 'text-right';
        if (currentRow === titleRowCount) {
          // header最后行不做children判断
          return setColumnProperties(item, key, keyIndex);
        } else {
          for (let j = 0; j <= childrenCount; j++) {
            for (let i = currentRow + 1; i <= titleRowCount; i++) {
              if (keyIndex + j <= finishKeyIndex) {
                continue;
              }
              if (tableData[i][keys[keyIndex + j]]?.value) {
                childrenList.push(setItemChildren(tableData[i][keys[keyIndex + j]], keys[keyIndex + j], keyIndex + j, i));
                break;
              } else {
                continue;
              }
            }
          }
          if (childrenList.length > 0) {
            item.key = key;
            item.children = childrenList;
          } else {
            setColumnProperties(item, key, keyIndex);
          }
          return item;
        }
      } else {
        return {};
      }
    };

    /***
     * 设定固定列
     * 默认第一列或第一列到最后未设定header name之间
     *
     */
    const setFixedColumn = (tableColumnObj) => {
      let firstItemList = tableData[0];

      for (let i = fixCount - 1; i >= 0; i--) {
        tableColumnObj.unshift({
          title: firstItemList[keys[i]].value ?? '',
          dataIndex: keys[i],
          width: setColumnWidth(keys[i], 0),
          fixed: 'left',
          key: keys[i],
          onCell: setColumnCell(keys[i], i),
          render: itemRenderFunction(),
        });
      }
      return tableColumnObj;
    };

    /***
     * 设定Table Columns
     *
     */
    const formatTableColumn = () => {
      let tableColumnObj = [];
      finishKeyIndex = fixCount - 1;
      for (let i = fixCount; i < keys.length; i++) {
        let nextItem = setItemChildren(tableData[0][keys[i]], keys[i], i, 0);
        if (nextItem.title === 'Action') {
          // 这个action是用来做场景跳转的， 注意和Action type区别
          nextItem.hidden = true;
        }
        if (nextItem.key) {
          tableColumnObj.push(nextItem);
        } else {
          continue;
        }
      }
      return tableColumnObj;
    };

    // 折叠列处理
    const expandableTableColumnWidth = () => {
      let tmpData = cloneDeep(tableData);
      let maxLength = 0;
      let tmpLength = 0;
      // 查找折叠部分中最宽单元格
      tmpData.splice(0, titleRowCount);
      for (let j = 0; j < tmpData.length; j++) {
        for (let i = 0; i < fixCount; i++) {
          if (tmpData[j][keys[i]]?.value) {
            tmpLength = formatValueToLength(tmpData[j][keys[i]].value);
            tmpLength = tmpLength + i * FIRST_COLUMN_LEVEL_BLANK;
            if (tmpLength > maxLength) {
              maxLength = tmpLength;
            }
          }
        }
      }
      maxLength = maxLength + TABLE_CELL_PADDING;
      let columnWidth = BASE_CHARTER_WIDTH * maxLength;
      tableWidth += columnWidth;
      return columnWidth;
    };

    const expandableTableFixedColumn = (tableColumnObj) => {
      let firstItemList = tableData[0];
      tableColumnObj.unshift({
        title: firstItemList[keys[0]].value ?? '',
        dataIndex: keys[0],
        width: expandableTableColumnWidth(),
        key: keys[0],
        fixed: 'left',
        render: itemRenderFunction(),
      });
      return tableColumnObj;
    };

    const tableColumns = resetColumnWidth(setFixedColumn(formatTableColumn()));

    switch (tableType) {
      case 'edit': {
        const columns = resetColumnWidth(setFixedColumn(formatTableColumn()));
        columns.push({
          title: '操作',
          dataIndex: 'operation',
          key: 'operation',
          width: 150,
          fixed: 'right',
          className: 'text-right',
        });

        return columns;
      }
      // 覆盖A类table处理方法
      // 有折叠
      case 'a':
      case 'tree':
        return resetColumnWidth(expandableTableFixedColumn(formatTableColumn()));
      // 覆盖action类table处理方法
      // 有折叠
      // 有action列
      case 'action':
      case 'treeAction':
        const actionMapping = tableOptions?.tableParams?.actionMapping ?? {};
        /***
         * 含有action单元格渲染方式
         *
         */
        const actionRenderFunction = () => {
          const getValue = (text) => {
            if (text) {
              const actions = text.split(dotReg);
              const actionContents = actions.map((action, index) => {
                const actionKey = action.trim();
                if (index > 0) {
                  return (
                    <>
                      <Space style={{ width: 6 }} />
                      {actionMapping[actionKey]}
                    </>
                  );
                }
                return actionMapping[actionKey];
              });
              return actionContents;
            }
            return '';
          };

          return (val, record, index) => {
            if (!val) {
              return '';
            }
            return val.bgColor ? (
              <div
                style={{
                  background: '#' + val.bgColor,
                }}
              >
                {getValue(val.value)}
              </div>
            ) : (
              getValue(val.value)
            );
          };
        };

        const setActionWidth = (key, keyIndex) => {
          let tmpData = cloneDeep(tableData);
          let maxLength = 0;
          let tmpLength = 0;

          // 查找当前列最宽内容
          for (let i = 0; i < tmpData.length; i++) {
            if (!tmpData[i][key]?.value || (keyIndex + 1 < keys.length && !tmpData[i][keys[keyIndex + 1]]?.value)) {
              continue;
            } else {
              tmpLength = tmpData[i][key].value.split(dotReg).length * 50;
              if (tmpLength > maxLength) {
                maxLength = tmpLength;
              }
            }
          }

          let columnWidth = maxLength;
          if (keyIndex >= fixCount) {
            columnWidthList.add(columnWidth);
          }
          tableWidth += columnWidth;
          return columnWidth;
        };

        const actionableTableFixedColumn = (tableColumnObj) => {
          const ACTION_KEY = 'アクション';
          const firstItemList = tableData[0];
          const key = Object.keys(firstItemList).find((key) => firstItemList[key].value === ACTION_KEY);
          const keyIndex = Object.keys(firstItemList).findIndex((key) => firstItemList[key].value === ACTION_KEY);
          if (keyIndex) {
            tableColumnObj.splice(keyIndex, 1, {
              title: ACTION_KEY,
              dataIndex: key,
              width: setActionWidth(key, keyIndex),
              key,
              fixed: 'right',
              render: actionRenderFunction(),
            });
          }

          return tableColumnObj;
        };

        return actionableTableFixedColumn(expandableTableFixedColumn(formatTableColumn()));
      case 'input':
        return tableColumns;
      case 'b': // todo 兼容历史
      case 'normal':
      // 默认table处理方法
      default:
        return tableColumns;
    }
  };

  const getTableDataSource = () => {
    /***
     * 普通单层样式的数据格式化
     *
     */
    const formatData = (data) => {
      let dataList = [];
      let tmpData = cloneDeep(data);
      tmpData.splice(0, titleRowCount + 1);
      for (let i = 0; i < tmpData.length; i++) {
        let obj = {};
        obj['key'] = i;
        for (let j = 0; j < keys.length; j++) {
          let cellValue = { value: '', bgColor: '' };
          cellValue.value = tmpData[i][keys[j]]?.value ?? '';
          cellValue.bgColor = tmpData[i][keys[j]]?.bgColor ?? '';
          obj[keys[j] + ''] = cellValue;
        }
        dataList.push(obj);
      }
      return dataList;
    };

    /***
     * 树形可折叠样式的数据格式化
     *
     */
    const setDataToTree = (data) => {
      let newDataObj = [];
      let parentLevelLastIndexList = [];
      // 初始化阶层数组[0, 0 ,0 ...]数组记录各阶层的最后一行数据下标
      for (let i = 0; i < fixCount; i++) {
        parentLevelLastIndexList.push(0);
      }
      data[0].key = 'A00';
      // 设定首行
      newDataObj.push(data[0]);
      for (let i = 1; i < data.length; i++) {
        // 获取当前行数据
        let tempDataObj = data[i];
        for (let j = 0; j < fixCount; j++) {
          // 若当前行首列有数据，则添加至顶层，更新当前顶层父级下标
          if (tempDataObj[keys[j]].value !== '' && j === 0) {
            data[i].key = 'A' + i + j;
            newDataObj.push(data[i]);
            parentLevelLastIndexList[0] = newDataObj.length - 1;
            break;
            // 获取当前行最靠前且有值的下标
          } else if (tempDataObj[keys[j]].value !== '' && j !== 0) {
            let g = 1;
            newDataObj[parentLevelLastIndexList[0]]['children'] = newDataObj[parentLevelLastIndexList[0]]['children'] ?? [];
            let tempObj = newDataObj[parentLevelLastIndexList[0]]['children'];
            while (g < j) {
              tempObj[parentLevelLastIndexList[g]]['children'] = tempObj[parentLevelLastIndexList[g]]['children'] ?? [];
              tempObj = tempObj[parentLevelLastIndexList[g]]['children'];
              g++;
            }
            // 将当前数据复制为当前数据组对应阶层子数据
            tempDataObj.key = 'A' + i + j;
            tempDataObj[keys[0]].value = tempDataObj[keys[j]].value;
            tempDataObj[keys[j]].value = '';
            tempObj.push(tempDataObj);
            parentLevelLastIndexList[g] = tempObj.length - 1;
            // 若当前数据非最深层数据，则说明当前为新阶层组
            // 低于当前层的阶层下标应从新初始化为0
            while (g < parentLevelLastIndexList.length - 1) {
              parentLevelLastIndexList[++g] = 0;
            }
            break;
          }
        }
      }
      return newDataObj;
    };

    switch (tableType) {
      case 'action': // todo 兼容历史
      case 'treeAction': // 有折叠 有action列
      case 'a': // todo 兼容历史
      case 'tree': {
        return setDataToTree(formatData(tableData));
      }
      case 'input':
      default: {
        // 默认table处理方法
        return formatData(tableData);
      }
    }
  };

  // 表格头部范围定义颜色，按需求调整
  const TITLE_AREA_COLOR = '806000';
  const tableType = tableOptions?.tableType ?? 'b';
  const disableSort = tableOptions?.disableSort ?? false;
  const keys = getKeys(tableData[0]);
  const fixCount = getFixCount();

  const titleRowCount = getTitleRowCount();
  const tableColumns = getTableColumns();
  const tableSource = getTableDataSource();

  return { tableColumns, tableSource, titleRowCount };
};
